import { useMutation, useQueryClient } from '@tanstack/react-query';

import { invalidateSupplierActivationQueries } from '../queryKeys';

import { postSupplierActivation } from './postSupplierActivation';
import { type PostSupplierActivationMutationOptions } from './types';

/**
 * React Query based custom hook for submitting a supplier activation request.
 *
 * Invalidates the activation request, supplier detail, and supplier overview
 * queries on success — the customer number in the payload is shared with and
 * persisted on the supplier detail page.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-overview/operation/post_supplier_activation_suppliers_actions__de_id__activation_post
 */
export const useMutationPostSupplierActivation = (
  options?: PostSupplierActivationMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deId, payload }) =>
      postSupplierActivation(deId, payload),
    onSettled(_data, _error, { deId }) {
      invalidateSupplierActivationQueries(queryClient, deId);
    },
    throwOnError: false,
    ...options,
  });
};
